import { Button } from "~/atoms/button";
import { Plus, CheckCircle } from "lucide-react";
import { WorkflowBuilderStep } from "~/molecules/WorkflowBuilderStep";
import { DragDropProvider, type DragDropEventHandlers } from "@dnd-kit/react";
import {
  WorkflowStepStatuses,
  type WorkflowDetails,
  type WorkflowStepInput,
} from "~/api/types";
import {
  TimelineStart,
  TimelineIndicator,
  StepIndicator,
} from "~/molecules/WorkflowTimelineIndicators";
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import { formatDate, dateDiff } from "~/helpers/date";
import { moveItemByIndex } from "~/helpers/moveItemByIndex";
import { isSortableOperation } from "@dnd-kit/react/sortable";

interface Props {
  isEditMode: boolean;
  workflow: WorkflowDetails;
  steps: WorkflowStepInput[];
  setSteps: Dispatch<SetStateAction<WorkflowStepInput[]>>;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  hideDueDates?: boolean;
}

type OnDragEnd = NonNullable<DragDropEventHandlers["onDragEnd"]>;

export function WorkflowBuilder({
  isEditMode,
  workflow,
  steps,
  setSteps,
  setHasUnsavedChanges,
  hideDueDates,
}: Props) {
  const { t } = useTranslation();
  const id = useId();
  const nextKeyRef = useRef(0);
  const stepsContainerRef = useRef<HTMLDivElement>(null);

  const handleUpdateStep = useCallback(
    (stepKey: string, updatedStep: WorkflowStepInput) => {
      setSteps((currentSteps) =>
        currentSteps.map((step) =>
          step._key === stepKey ? { ...updatedStep, _key: stepKey } : step,
        ),
      );
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges, setSteps],
  );

  const reorderByIndex = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      setSteps((currentSteps) => {
        if (
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= currentSteps.length ||
          toIndex >= currentSteps.length
        ) {
          return currentSteps;
        }

        return moveItemByIndex(currentSteps, fromIndex, toIndex);
      });
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges, setSteps],
  );

  const handleRemoveStep = useCallback(
    (stepKey: string) => {
      setSteps((currentSteps) =>
        currentSteps.filter((step) => step._key !== stepKey),
      );
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges, setSteps],
  );

  const handleAddStep = useCallback(() => {
    setSteps((currentSteps) => [
      ...currentSteps,
      {
        _key: `${id}-${nextKeyRef.current++}`,
        name: `Step ${currentSteps.length + 1}`,
        description: "",
        status: WorkflowStepStatuses.upcoming,
        type: "review",
        order: currentSteps.length,
        dueAt: null,
        users: [],
      },
    ]);
    setHasUnsavedChanges(true);

    // Scroll to the newly added step once it renders
    setTimeout(() => {
      stepsContainerRef.current?.lastElementChild?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 100);
  }, [id, setHasUnsavedChanges, setSteps]);

  const stepsWithDueWarning = useMemo(() => {
    const dueWarnings = new Set<number>();
    let latestSoFar = formatDate(steps[0]?.dueAt, "date");
    for (let i = 1; i < steps.length; i++) {
      if (!steps[i]?.dueAt) continue;
      const dueAt = formatDate(steps[i]?.dueAt, "date");
      if (dateDiff(dueAt, latestSoFar, "day") < 0) {
        dueWarnings.add(i);
      } else {
        latestSoFar = dueAt;
      }
    }
    return dueWarnings;
  }, [steps]);

  const handleDragEndEvent = useCallback<OnDragEnd>(
    (event) => {
      if (event.canceled) return;
      if (!isSortableOperation(event.operation)) return;

      const source = event.operation.source;
      if (!source) return;

      reorderByIndex(source.initialIndex, source.index);
    },
    [reorderByIndex],
  );

  return (
    <div className="relative space-y-8">
      <div className="from-primary/40 via-primary/30 to-primary/40 absolute top-0 bottom-0 left-6 z-0 w-0.5 bg-linear-to-b" />
      <TimelineStart
        startedAt={workflow.stepInstances[0]?.createdAt}
        scheduleReviewFrom={workflow.document.scheduleReviewFrom}
      />

      <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-8">
        <div className="grid gap-8">
          {steps.map((step, index) => (
            <div key={step._key} className="z-10">
              <StepIndicator value={`${index + 1}`} status={step.status} />
            </div>
          ))}
        </div>
        <div className="grid gap-8" ref={stepsContainerRef}>
          <DragDropProvider onDragEnd={handleDragEndEvent}>
            {steps.map((step, index) => (
              <WorkflowBuilderStep
                key={step._key}
                step={step}
                index={index}
                onRemove={handleRemoveStep}
                onUpdate={handleUpdateStep}
                isEditMode={isEditMode}
                categoryId={workflow.document.category.id}
                showDueWarning={stepsWithDueWarning.has(index)}
                hideDueDates={hideDueDates}
              />
            ))}
          </DragDropProvider>
        </div>
      </div>

      {isEditMode && (
        <div className="flex items-center justify-center">
          <Button variant="secondary" onClick={handleAddStep} size="sm">
            <Plus className="h-5 w-5" />
            {t(($) => $.commons.workflow.addStep)}
          </Button>
        </div>
      )}

      <TimelineIndicator
        icon={CheckCircle}
        text={
          workflow.documentVersion.reviewTo
            ? `${t(($) => $.commons.workflow.due)} ${formatDate(workflow.documentVersion.reviewTo, "date")}`
            : undefined
        }
      />
    </div>
  );
}
